import { Component } from '@angular/core';
import { AuthorizedUsersDialogComponent } from '@components/authorized-users-dialog/authorized-users-dialog.component';
import { Question } from '@models/votes/question.entity';
import { UserAnswer } from '@models/votes/user-answer.entity';
import { Vote } from '@models/votes/vote.entity';
import { AccountService } from '@services/account.service';
import { AuthorizationService } from '@services/authorization.service';
import { MessageService } from '@services/message.service';
import { QuestionService } from '@services/question.service';
import { clone } from '@utils/clone.function';
import { isEmpty } from '@utils/is-empty.function';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError } from 'rxjs';

@Component({
  selector: 'ebs-question',
  templateUrl: './question.component.html',
  styleUrls: ['./question.component.scss'],
})
export class QuestionComponent {
  public questions: Question[] = [];
  public allVotes: UserAnswer[] = [];
  public selectedQuestion: Question | null = null;

  public isVoteVisible: boolean = false;
  public isConfirmation: boolean = false;
  public editIndex: number = -1;

  public whiteTemplate = (): Vote => {
    return {
      answer: 'Scheda bianca',
      isWhite: true,
      order: 0,
    };
  };

  public blankTemplate = (order: number): Vote => {
    return {
      answer: '',
      isWhite: false,
      order,
    };
  };

  public items: MenuItem[] = [
    {
      command: () => this.addNewQuestion(),
      icon: PrimeIcons.PLUS,
    },
  ];

  constructor(
    private questionService: QuestionService,
    private authService: AuthorizationService,
    private accountService: AccountService,
    public dialogService: DialogService,
    private messageService: MessageService,
  ) {
    this.loadQuestions();
  }

  loadQuestions() {
    this.questionService.getEncryptedVotes().subscribe((result) => {
      this.allVotes = result;
    });
    this.questionService.getQuestionList().subscribe((result: Question[]) => {
      this.questions = result;
      this.questions.forEach((elem) => {
        if (elem.possibleVotes.findIndex((vote) => vote.isWhite) > -1) {
          elem['hasWhite'] = true;
        }
      });
    });
  }

  editQuestion(question: Question | null) {
    this.selectedQuestion = clone(question);
  }

  getVotes(subscriberId: number) {
    let votes: Vote[] = <Vote[]>(
      JSON.parse(
        atob(
          this.allVotes.find(
            (vote) =>
              vote.questionId === this.selectedQuestion?.questionId &&
              vote.userId === subscriberId,
          )?.encryptedBase64Answer || '',
        ) || '[{"answer": "Nessun voto espresso"}]',
      )
    );
    let votesString = '';
    for (let i = 0; i < votes.length; i++) {
      let v = votes[i];
      votesString = votesString + v.answer;
      if (votes.length - 1 > i) {
        votesString = votesString + ', ';
      }
    }
    return votesString;
  }

  cancel(index: number) {
    if (isEmpty(this.questions[index].questionId)) {
      this.questions.pop();
    } else {
      this.questions[index] = clone(this.selectedQuestion);
    }
    this.selectedQuestion = null;
  }

  modifyQuestion(index: number) {
    this.saveGroupChanges(this.questions[index]);
  }

  saveGroupChanges(question: Question) {
    this.questionService
      .modifyQuestion([question])
      .pipe(
        catchError((err) => {
          this.loadQuestions();
          this.selectedQuestion = null;
          this.editIndex = -1;
          this.messageService.error(
            'Modifica votazione',
            'Errore durante la modifica',
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.loadQuestions();
        this.selectedQuestion = null;
        this.editIndex = -1;
        this.messageService.success(
          'Modifica votazione',
          'Modifica avvenuta con successo',
        );
      });
  }

  removeQuestion(question: Question, index: number) {
    this.questionService
      .remveQuestion([question])
      .pipe(
        catchError((err) => {
          this.selectedQuestion = null;
          this.messageService.error(
            'Eliminazione votazione',
            "Errore durante l'eliminazione",
          );
          return err;
        }),
      )
      .subscribe((res) => {
        this.questions.splice(index, 1);
        this.selectedQuestion = null;
        this.messageService.success(
          'Eliminazione votazione',
          'Eliminazione avvenuta con successo',
        );
      });
  }

  modifyAuthorizedUsers(index: number) {
    this.accountService.getAuthList().subscribe((result) => {
      let signs = this.questions[index].authorizations?.map(
        (elem) => elem.signId,
      );
      let dialogRef: DynamicDialogRef = this.dialogService.open(
        AuthorizedUsersDialogComponent,
        {
          maximizable: false,
          draggable: false,
          height: '100%',
          width: '100%',
          closable: true,
          closeOnEscape: false,
          data: {
            selectedUsers: result.filter((elem: any) =>
              signs?.includes(elem.signId),
            ),
            allUsers: result,
            allowSimplifiedView: true,
          },
        },
      );

      dialogRef.onClose.subscribe((result) => {
        if (!result) {
          return;
        }
        this.questions[index].authorizations = result;
      });
    });
  }

  addNewAnswer(index: number, template: Vote, start: number = 0) {
    this.questions[index]?.possibleVotes.splice(start, 0, clone(template));
  }

  removeAnswer(index: number, vIndex: number) {
    this.questions[index]?.possibleVotes.splice(vIndex, 1);
  }

  onWhiteLabelChange(index: number, event: boolean) {
    if (event) {
      this.addNewAnswer(index, this.whiteTemplate());
    } else {
      this.removeAnswer(
        index,
        this.questions[index]?.possibleVotes.findIndex((elem) => elem.isWhite),
      );
    }
  }

  addNewQuestion() {
    this.isConfirmation = false;
    this.questions.push({
      questionId: null,
      description: '',
      title: '',
      isVisible: true,
      maxResponses: 1,
      minResponses: 1,
      endTime: new Date(),
      possibleVotes: [],
      startTime: new Date(),
    });

    this.selectedQuestion = this.questions[this.questions.length - 1];
  }

  showVotes(question: Question) {
    this.selectedQuestion = question;
    this.isVoteVisible = true;
  }

  hideVotes() {
    this.selectedQuestion = null;
    this.isVoteVisible = false;
  }

  showConfirm(question: Question, index: number) {
    this.selectedQuestion = question;
    this.editIndex = index;
    this.isConfirmation = true;
  }

  hideConfirm() {
    this.isConfirmation = false;
  }
}
